/**
 * 카테고리 검색 — 서버 호출 없이 정적 카탈로그에서 처리한다.
 *
 * 하나를 강제하지 않고 관련 카테고리를 여러 개 제시한다(계획 §8). 사용자가
 * "기계식 키보드"를 검색해도 공식 카테고리 `키보드`로 이어지게 하는 것이 목적이고,
 * 그 연결은 카테고리를 쪼개는 대신 `keywords`가 담당한다.
 *
 * 이 파일이 `shared`에 있는 이유: 검색 품질이 서비스의 첫인상인데 화면 코드에
 * 묻히면 회귀를 잡을 수 없다. 여기 있으면 고정 질의 세트로 단위 테스트가 된다.
 *
 * 카테고리를 평탄하게 만든 뒤로 **검색이 카테고리를 찾는 유일한 길이다.**
 * 목록을 훑어 고르는 화면이 없으므로, 여기서 못 찾으면 사용자에게는 그 카테고리가
 * 없는 것과 같다. 그래서 `keywords`가 예전보다 더 중요해졌다.
 */

import { isPickable } from './id.js';
import type { CategoryStatus } from './types.js';

export interface SearchableCategory {
  id: string;
  name: string;
  status: CategoryStatus;
  keywords?: string[];
}

export type MatchField = 'name' | 'keyword';

export interface SearchResult<T extends SearchableCategory> {
  node: T;
  score: number;
  /** 어디서 걸렸는지 — 화면에서 "'기계식 키보드'로 검색됨" 같은 힌트에 쓴다 */
  matchedOn: MatchField;
  /** 실제로 매치된 문자열. name이면 이름, keyword면 그 키워드 */
  matchedText: string;
}

export interface SearchOptions {
  /** 기본 20 */
  limit?: number;
}

const SCORE = {
  nameExact: 100,
  namePrefix: 80,
  keywordExact: 70,
  namePartial: 50,
  /**
   * 질의가 이름을 **품고** 있을 때. "기계식 키보드"로 `키보드`를 찾는 경우다.
   *
   * 사람들은 수식어를 붙여서 친다. 이게 없으면 이름보다 긴 질의는 전부 빈손이
   * 되고(매칭이 `이름.includes(질의)`라서), 그 구멍을 메우려고 `기계식 키보드`·
   * `게이밍 키보드`·`무선 키보드`를 전부 키워드로 적어야 했다 — 카테고리마다,
   * 언어마다. 규칙 하나로 대신한다.
   *
   * 점수를 namePartial보다 낮게 둔 이유: 이름에 직접 걸린 것이 항상 위여야 한다.
   */
  nameInQuery: 45,
  keywordPrefix: 40,
  keywordPartial: 30,
  /**
   * 질의가 키워드를 품을 때. "포켓몬 카드"로 `트레이딩 카드`,
   * "에어팟 프로"로 `이어폰`, "인텔리제이 단축키"로 `IDE·에디터`를 찾는 경우다.
   *
   * 가장 약한 신호라 제일 아래 둔다. 이름 쪽보다 **글자 수 문턱을 높게** 잡는다
   * (아래 MIN_CONTAINED_KEYWORD) — 키워드에는 `옷`·`차`처럼 짧은 말이 섞여 있어
   * 그대로 열면 "옷장 정리"·"자동차 보험" 같은 질의에 걸린다.
   */
  keywordInQuery: 25,
} as const;

/**
 * 비교용 정규화: NFKC(전각·호환문자 통일) + 소문자 + 앞뒤 공백 제거 +
 * 내부 연속 공백을 하나로. "ＫＥＹ"와 "key", "기계식  키보드"와 "기계식 키보드"가
 * 같아진다.
 */
export function normalizeForSearch(value: string): string {
  return value.normalize('NFKC').toLowerCase().trim().replace(/\s+/g, ' ');
}

/** 공백을 전부 없앤 형태. "기계식키보드"로 검색해도 걸리게 한다. */
function compact(value: string): string {
  return normalizeForSearch(value).replace(/\s/g, '');
}

function scoreAgainst(target: string, query: string, field: MatchField): number {
  const normalizedTarget = normalizeForSearch(target);
  const exact = field === 'name' ? SCORE.nameExact : SCORE.keywordExact;
  const prefix = field === 'name' ? SCORE.namePrefix : SCORE.keywordPrefix;
  const partial = field === 'name' ? SCORE.namePartial : SCORE.keywordPartial;

  if (normalizedTarget === query) {
    return exact;
  }
  if (normalizedTarget.startsWith(query)) {
    return prefix;
  }
  if (normalizedTarget.includes(query)) {
    return partial;
  }

  // 공백 차이만으로 놓치지 않게 마지막으로 한 번 더 본다.
  const compactTarget = compact(target);
  const compactQuery = compact(query);
  if (compactQuery.length > 0 && compactTarget.includes(compactQuery)) {
    return partial;
  }

  /*
   * 반대 방향 — 질의가 이름을 품는가. "기계식 키보드" ⊃ "키보드".
   *
   * 이름에만 적용한다. 키워드에까지 열면 `키캡`을 품은 긴 질의가 엉뚱한
   * 카테고리를 끌고 온다.
   *
   * 두 글자 미만은 제외한다 — 한 글자 이름("차")이 아무 질의에나 걸린다.
   */
  const minContained = field === 'name' ? MIN_CONTAINED_NAME : MIN_CONTAINED_KEYWORD;
  if (compactTarget.length >= minContained && compactQuery.includes(compactTarget)) {
    return field === 'name' ? SCORE.nameInQuery : SCORE.keywordInQuery;
  }

  return 0;
}

/** 질의에 품길 수 있는 이름의 최소 길이 */
const MIN_CONTAINED_NAME = 2;

/**
 * 키워드 쪽 문턱. 이름보다 높다 — 키워드에는 `옷`·`차`처럼 짧은 말이 섞여 있어,
 * 같은 문턱을 쓰면 "옷장 정리"가 티셔츠를, "자동차 보험"이 차를 끌고 온다.
 */
const MIN_CONTAINED_KEYWORD = 3;

export function searchCategories<T extends SearchableCategory>(
  query: string,
  nodes: readonly T[],
  options: SearchOptions = {},
): SearchResult<T>[] {
  const normalizedQuery = normalizeForSearch(query);
  // 빈 질의에 전체 목록을 주지 않는다 — 검색과 탐색은 다른 기능이다.
  if (normalizedQuery.length === 0) {
    return [];
  }

  const limit = options.limit ?? 20;
  const results: SearchResult<T>[] = [];

  for (const node of nodes) {
    // deprecated는 검색에 노출하지 않는다. 기존 Pick은 그대로 보이지만
    // 새로 고르게 하지는 않는다(계획 §8).
    if (node.status !== 'active') {
      continue;
    }
    // 카탈로그는 CloudFront에서 받는다 — 캐시가 오래된 브라우저는 그룹이
    // 남아 있던 시절의 파일을 들고 있을 수 있다. 그때 그룹을 결과에 내보내면
    // 고를 수는 있는데 서버가 거부하는 막다른 길이 된다.
    if (!isPickable(node.id)) {
      continue;
    }

    let best: SearchResult<T> | null = null;

    const nameScore = scoreAgainst(node.name, normalizedQuery, 'name');
    if (nameScore > 0) {
      best = { node, score: nameScore, matchedOn: 'name', matchedText: node.name };
    }

    for (const keyword of node.keywords ?? []) {
      const keywordScore = scoreAgainst(keyword, normalizedQuery, 'keyword');
      if (keywordScore > 0 && (best === null || keywordScore > best.score)) {
        best = { node, score: keywordScore, matchedOn: 'keyword', matchedText: keyword };
      }
    }

    if (best !== null) {
      results.push(best);
    }
  }

  results.sort((a, b) => {
    if (a.score !== b.score) {
      return b.score - a.score;
    }
    // 점수가 같으면 이름이 짧은 쪽이 더 정확한 매치다("키보드" > "무선 키보드").
    if (a.node.name.length !== b.node.name.length) {
      return a.node.name.length - b.node.name.length;
    }
    // 마지막은 id로 — 순서가 실행마다 달라지면 테스트도 화면도 흔들린다.
    return a.node.id.localeCompare(b.node.id);
  });

  return results.slice(0, limit);
}
